import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import { Typography, Box, Button } from '@mui/material';
import { FlexBox } from 'components/Containers/FlexBox';
import { getCategoryStats } from './utils';
import { Circle } from '@mui/icons-material';
import useAppTheme from 'hooks/useAppTheme';
import { Selector } from 'components/Form/Selectors/Selector';
import React, { useMemo, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const MAX_CHAR = 12;

const sortOptions = [
  { id: 'cost', display: 'Total cost' },
  { id: 'count', display: 'Total count' },
];

const CustomYAxisTick = memo(({ x, y, payload, fontColor }: any) => {
  const [line1Raw, line2Raw] = (payload?.value || '').split('-');
  const line1 = line1Raw?.trim() || '';
  const line2 = line2Raw?.trim() || '';
  const isSame = line1.toLowerCase() === line2.toLowerCase();

  const truncate = (text: string) =>
    text.length > MAX_CHAR ? text.slice(0, MAX_CHAR - 3) + '...' : text;

  const displayLine1 = truncate(line1);
  const displayLine2 = isSame ? '' : truncate(line2);

  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill={fontColor || '#666'}
      fontSize={11}
    >
      <tspan x={x} dy={0}>
        {displayLine1}
      </tspan>
      {displayLine2 && (
        <tspan x={x} dy={12} textAnchor="left">
          {displayLine2}
        </tspan>
      )}
      <title>{line2 ? `${line1} - ${line2}` : line1}</title>
    </text>
  );
});

CustomYAxisTick.displayName = 'CustomYAxisTick';

const CustomLegend = memo(() => {
  const { t } = useTranslation();
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const legendItems = useMemo(
    () => [
      { label: t('count'), color: palette.primary },
      { label: t('cost'), color: '#58BF6A' },
    ],
    []
  );

  return (
    <Box display="flex" gap={1}>
      {legendItems.map((item) => (
        <Box key={item.label} display="flex" alignItems="center" gap="2px">
          <Circle sx={{ fontSize: 14, color: item.color }} />
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              color: palette.textPrimary,
            }}
          >
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
});

CustomLegend.displayName = 'CustomLegend';

const CustomRowHighlight = memo(
  ({
    x,
    y,
    width,
    height,
    fill,
    payload,
    selectedCategory,
    chartWidth,
  }: any) => {
    const isSelected = payload.category === selectedCategory;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          fill={fill}
          rx={4}
          cursor={'pointer'}
        />
        {isSelected && (
          <rect
            x={x - 1} // highlight entire chart row
            y={y - 2}
            width={chartWidth || 0}
            height={(height as number) + 4}
            fill="none"
            stroke="#FFA500"
            strokeWidth={1}
            rx={4}
            strokeDasharray="4 2"
          />
        )}
      </g>
    );
  }
);

CustomRowHighlight.displayName = 'CustomRowHighlight';

const AssetChart = ({
  enableDarkTheme,
  data,
  selectedAssets,
  selectedPart,
  selectedYAxisCategory,
  setSelectedYAxisCategory,
  sortType,
  setSortType,
}: {
  enableDarkTheme: boolean;
  data: any[];
  selectedAssets: any[];
  selectedPart: any[];
  sortType: string;
  setSortType: React.Dispatch<React.SetStateAction<'cost' | 'count'>>;
  selectedYAxisCategory: string;
  setSelectedYAxisCategory: React.Dispatch<React.SetStateAction<string>>;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const cursorFill =
    CLIENT_THEME === 'yokohama'
      ? palette.sectionBackground
      : enableDarkTheme
      ? '#1B2B4A'
      : '#EEEEEE';
  const { t } = useTranslation();
  const handleBarClick = useCallback(
    (data: any) => {
      if (data?.category) setSelectedYAxisCategory(data.category);
    },
    [setSelectedYAxisCategory]
  );

  const formatCategoryText = useCallback((text: string) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  }, []);

  // Filter only replacement actions & selected assets
  const assetsData = useMemo(() => {
    return data.filter((item: any) => {
      const subCategory = item.sub_component_categories.replace(/^\[|\]$/g, '');
      return (
        selectedPart.includes(`${item.components_category} - ${subCategory}`) &&
        selectedAssets.includes(item.asset_id) &&
        item.actions_performed === 'REPLACE'
      );
    });
  }, [data, selectedAssets, selectedPart]);

  // Process category stats
  const categoryData = useMemo(
    () => getCategoryStats(assetsData, sortType === 'count'),
    [assetsData, sortType]
  );

  // Process data for chart
  const processedData = useMemo(() => {
    return categoryData.map((item) => {
      const [component, subcomponent] = item.category.split('_');
      const formattedComponent = formatCategoryText(component || '');
      const formattedSubcomponent = formatCategoryText(subcomponent || '');
      return {
        ...item,
        component: formattedComponent,
        subcomponent: formattedSubcomponent,
        label: `${formattedComponent}\n${formattedSubcomponent}`,
        cost: item?.cost > 0 ? item.cost : 0,
        count: item?.count > 0 ? item.count : 0,
      };
    });
  }, [categoryData, selectedPart, formatCategoryText]);

  // Memoize tooltip formatter
  const tooltipFormatter = useCallback((value: any, name: string) => {
    return [
      name === 'Cost' ? `$${Number(value).toLocaleString()}` : value,
      name,
    ];
  }, []);

  return (
    <>
      <Box
        sx={{
          marginTop: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: 1,
          paddingTop: 2,
        }}
      >
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', px: 2 }}
          alignItems={'center'}
          pb={2}
        >
          <Box>
            <Typography sx={{ color: palette.textPrimary, fontSize: 14 }}>
              {t('frequently_replaced_parts')}
            </Typography>
            <Typography sx={{ color: palette.textSecondary, fontSize: 10 }}>
              {t(
                'click_on_bar_to_view_details_of_a_particular_component_subco'
              )}
            </Typography>
          </Box>

          <CustomLegend />

          <Box display="flex" flexDirection="row" gap={2} alignItems="end">
            <Box display="flex" gap={1} alignItems="center">
              <Typography sx={{ color: palette.textPrimary, fontSize: 12 }}>
                {t('sort_by')}
              </Typography>
              <Selector
                minWidth="1px"
                value={sortType}
                onChange={(e) => {
                  setSortType(e.target.value as 'cost' | 'count');
                }}
                selectorOptions={sortOptions}
                enableDarkTheme={enableDarkTheme}
              />
            </Box>
            <Box>
              {selectedYAxisCategory ? (
                <Button
                  onClick={() => {
                    setSelectedYAxisCategory('');
                  }}
                  variant="contained"
                  color="secondary"
                  sx={{
                    height: '28px',
                    minHeight: '28px',
                    textTransform: 'none',
                  }}
                >
                  Reset
                </Button>
              ) : null}
            </Box>
          </Box>
        </Box>

        <FlexBox
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {processedData.length === 0 ? (
            <Typography sx={{ color: palette.textPrimary }}>
              {t('no_data_for_selected_filter')}
            </Typography>
          ) : (
            <Box width="100%" height={'400px'} overflow="scroll">
              <ResponsiveContainer
                width="100%"
                height={
                  processedData.length < 5 ? 5 * 80 : processedData.length * 80
                }
              >
                <BarChart
                  data={processedData}
                  layout="vertical"
                  margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                  barCategoryGap={20}
                  barGap={4}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal
                    vertical={false}
                    stroke={palette.cartesianGrid}
                  />

                  <XAxis
                    type="number"
                    dataKey="cost"
                    orientation="top"
                    xAxisId="cost"
                    tick={{ fontSize: 11, fill: palette.textSecondary }}
                  />
                  <XAxis
                    type="number"
                    dataKey="count"
                    orientation="bottom"
                    xAxisId="count"
                    tick={{ fontSize: 11, fill: palette.textSecondary }}
                  />

                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={<CustomYAxisTick fontColor={palette.textSecondary} />}
                    width={80}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: enableDarkTheme
                        ? palette.boxBackground
                        : palette.boxBackground,
                      border: `1px solid ${
                        enableDarkTheme
                          ? palette.cardBorderColor
                          : palette.cardBorderColor
                      }`,
                      borderRadius: 4,
                      padding: '6px 10px',
                      boxShadow: 'none',
                    }}
                    labelStyle={{
                      color: palette.textPrimary,
                      fontSize: 12,
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                    itemStyle={{
                      color: palette.textPrimary,
                      fontSize: 12,
                      padding: '2px 0',
                    }}
                    formatter={tooltipFormatter}
                    cursor={{
                      fill: cursorFill,
                      opacity: 0.3,
                    }}
                  />

                  {['count', 'cost'].map((key) => (
                    <Bar
                      key={key}
                      dataKey={key}
                      name={key === 'count' ? 'Count' : 'Cost'}
                      fill={key === 'count' ? palette.primary : '#58BF6A'}
                      barSize={20}
                      xAxisId={key}
                      radius={[0, 4, 4, 0]}
                      onClick={handleBarClick}
                      shape={(props: any) => (
                        <CustomRowHighlight
                          {...props}
                          chartWidth={props.width}
                          selectedCategory={selectedYAxisCategory}
                        />
                      )}
                    >
                      <LabelList
                        dataKey={key}
                        position="right"
                        formatter={
                          key === 'cost'
                            ? (v: any) => `$${Number(v).toLocaleString()}`
                            : undefined
                        }
                        style={{
                          fontSize: 11,
                          fill: palette.textPrimary,
                        }}
                      />
                    </Bar>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </FlexBox>
      </Box>
    </>
  );
};

export default AssetChart;
